import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { fetchCartDetails } from "utils/cartUtils";

/**
 * Cart items enriched with their product details.
 *
 * `state.cart.items` deliberately stores no display fields (`file_url`,
 * `displayname`, `price`, `pricing`), so every surface that renders cart
 * contents has to re-fetch them. This hook is that fetch: it re-runs
 * `fetchCartDetails` whenever the cart or the customer's category changes. The
 * category matters because restricted promotions are filtered at fetch time — a cart
 * loaded before login would otherwise keep the guest's promotion-free data.
 *
 * @returns {{ cartDetails: object[], cartDetailsLoading: boolean }}
 */
export default function useCartDetails() {
  const cartItems = useSelector((state) => state.cart.items);
  const customerCategory = useSelector((state) => state.user.info?.category);
  const [cartDetails, setCartDetails] = useState([]);
  const [cartDetailsLoading, setCartDetailsLoading] = useState(false);

  useEffect(() => {
    const loadCartDetails = async () => {
      if (cartItems.length === 0) {
        setCartDetails([]);
        setCartDetailsLoading(false);
        return;
      }

      try {
        setCartDetailsLoading(true);
        const details = await fetchCartDetails(cartItems);
        setCartDetails(details);
      } finally {
        setCartDetailsLoading(false);
      }
    };

    loadCartDetails();
  }, [cartItems, customerCategory]);

  return { cartDetails, cartDetailsLoading };
}
